import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * <SEO> — per-page meta tags + OG + Twitter + canonical + optional JSON-LD.
 *
 * Usage:
 *   <SEO
 *     title="Aerial Yoga Teacher Training · Aviana Yoga"
 *     description="Yoga Alliance 50hr aerial yoga teacher training with Holly Johnson…"
 *     path="/trainings/aerial-yoga"
 *     image="https://…"
 *     keywords="aerial yoga teacher training, aerial yoga certification, YACEP"
 *     jsonLd={{ '@type': 'Course', name: '…' }}
 *   />
 *
 * If `title` omits "Aviana Yoga", we append it for you.
 */
const SITE = "https://avianayoga.com";
// Site-wide social preview. Points at the /api/og-image endpoint which
// resolves to Holly's currently-chosen photo (settable from Admin →
// Settings) with a static fallback baked into the frontend build.
const DEFAULT_IMAGE = `${SITE}/api/og-image`;

/** Convert "/social/og-foo.jpg" → "https://avianayoga.com/social/og-foo.jpg". */
function absoluteUrl(src) {
  if (!src) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(src)) return src;
  return `${SITE}${src.startsWith("/") ? src : `/${src}`}`;
}

export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  keywords,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  const fullTitle = title
    ? (/aviana/i.test(title) ? title : `${title} · Aviana Yoga`)
    : "Aviana Yoga — Aerial Yoga Teacher Training, Retreats & Reiki with Holly Johnson";
  const url = `${SITE}${path.startsWith("/") ? path : `/${path}`}`;
  const ogImage = absoluteUrl(image);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />

      {/* Robots */}
      {noindex
        ? <meta name="robots" content="noindex, nofollow" />
        : <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Aviana Yoga" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Per-page JSON-LD — accepts either a single object or an array of
          objects (e.g. Course + FAQ + Video + Breadcrumb). Each gets its
          own <script> block so Google can parse them independently. */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((blob, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(blob)}
        </script>
      ))}
    </Helmet>
  );
}
